import { SystemEntity } from '@erp/shared-types';
import { cn } from '@/lib/utils';
import { useAPInvoiceExtractionResult } from '@/api/rpc/hooks/accounts-payable/useAPInvoices';
import { ScanIcon } from '@phosphor-icons/react';
import { Badge } from '@/components/ui/badge';
import { SystemEntityInline } from '@/components/system-entities/inline-ui';
import { T } from 'gt-react';

interface APInvoiceExtractionResultPreviewProps {
  entityType: SystemEntity;
  entityId: string;
  className?: string;
}

export function APInvoiceExtractionResultPreview({
  entityId,
  className,
}: APInvoiceExtractionResultPreviewProps) {
  const { extractionResult, isLoading, error } = useAPInvoiceExtractionResult(
    entityId,
    true,
  );

  if (isLoading) {
    return (
      <div className={cn('min-w-64 max-w-sm p-3', className)}>
        <div className="mb-2 flex items-center gap-2">
          <ScanIcon className="size-4 text-muted-foreground" />
          <h4 className="text-sm font-semibold">
            <T>AP Invoice Extraction Result</T>
          </h4>
        </div>
        <p className="text-xs text-muted-foreground">
          <T>Loading...</T>
        </p>
      </div>
    );
  }

  if (error || !extractionResult) {
    return (
      <div className={cn('min-w-64 max-w-sm p-3', className)}>
        <div className="mb-2 flex items-center gap-2">
          <ScanIcon className="size-4 text-muted-foreground" />
          <h4 className="text-sm font-semibold">
            <T>AP Invoice Extraction Result</T>
          </h4>
        </div>
        <p className="text-xs text-muted-foreground">
          <T>Unable to load extraction result details.</T>
        </p>
      </div>
    );
  }

  return (
    <div className={cn('min-w-64 max-w-sm p-3', className)}>
      <div className="mb-3 flex items-center gap-2">
        <ScanIcon className="size-4 text-muted-foreground" />
        <h4 className="truncate text-sm font-semibold">
          <T>Extraction Result</T>
        </h4>
      </div>

      <div className="space-y-2 text-xs">
        <div>
          <Badge
            variant={
              extractionResult.status === 'success' ? 'default' : 'destructive'
            }
            className="text-xs"
          >
            {extractionResult.status}
          </Badge>
        </div>

        <div>
          <span className="text-muted-foreground">
            <T>Model:</T>
          </span>
          <span className="font-medium">{extractionResult.modelUsed}</span>
        </div>

        {extractionResult.documentId && (
          <div>
            <span className="text-muted-foreground">
              <T>Document:</T>
            </span>
            <SystemEntityInline
              entityType={SystemEntity.CORE_DOCUMENT}
              entityId={extractionResult.documentId}
              className="text-xs"
            />
          </div>
        )}

        {extractionResult.apInvoiceId && (
          <div>
            <span className="text-muted-foreground">
              <T>AP Invoice:</T>
            </span>
            <SystemEntityInline
              entityType={SystemEntity.AP_INVOICE}
              entityId={extractionResult.apInvoiceId}
              className="text-xs"
            />
          </div>
        )}
      </div>
    </div>
  );
}
